<template>
	<div id="viewport" :class="viewportClasses" role="tablist">
		<Sidebar v-if="store.state.appLoaded" :overlay="overlay" />
		<div
			id="sidebar-overlay"
			ref="overlay"
			aria-hidden="true"
			@click="store.commit('sidebarOpen', false)"
		/>
		<div
			id="userlist-overlay"
			aria-hidden="true"
			@click="store.commit('userlistOpen', false)"
		/>
		<router-view ref="loungeWindow"></router-view>
		<Mentions />
		<ImageViewer ref="imageViewer" />
		<ContextMenu ref="contextMenu" />
		<ConfirmDialog ref="confirmDialog" />
		<WhoisModal />
		<div id="upload-overlay"></div>
		<audio ref="lafcheSilentAudio" loop preload="auto" style="display: none" />
	</div>
</template>

<script lang="ts">
import constants from "../js/constants";
import eventbus from "../js/eventbus";
import Mousetrap, {ExtendedKeyboardEvent} from "mousetrap";
import throttle from "lodash/throttle";
import storage from "../js/localStorage";
import isIgnoredKeybind from "../js/helpers/isIgnoredKeybind";
import socket from "../js/socket";
import {switchToChannel} from "../js/router";
import {suppressWhoisModalFor} from "../js/helpers/contextMenu";

import Sidebar from "./Sidebar.vue";
import ImageViewer from "./ImageViewer.vue";
import ContextMenu from "./ContextMenu.vue";
import ConfirmDialog from "./ConfirmDialog.vue";
import Mentions from "./Mentions.vue";
import WhoisModal from "./WhoisModal.vue";
import {
	computed,
	provide,
	defineComponent,
	onBeforeUnmount,
	onMounted,
	ref,
	Ref,
	InjectionKey,
} from "vue";
import {useStore} from "../js/store";
import type {DebouncedFunc} from "lodash";

export const imageViewerKey = Symbol() as InjectionKey<Ref<typeof ImageViewer | null>>;
const contextMenuKey = Symbol() as InjectionKey<Ref<typeof ContextMenu | null>>;
const confirmDialogKey = Symbol() as InjectionKey<Ref<typeof ConfirmDialog | null>>;

export default defineComponent({
	name: "App",
	components: {
		Sidebar,
		ImageViewer,
		ContextMenu,
		ConfirmDialog,
		Mentions,
		WhoisModal,
	},
	setup() {
		const store = useStore();
		const overlay = ref(null);
		const loungeWindow = ref(null);
		const imageViewer = ref(null);
		const contextMenu = ref(null);
		const confirmDialog = ref(null);

		provide(imageViewerKey, imageViewer);
		provide(contextMenuKey, contextMenu);
		provide(confirmDialogKey, confirmDialog);

		const viewportClasses = computed(() => {
			return {
				notified: store.getters.highlightCount > 0,
				"menu-open": store.state.appLoaded && store.state.sidebarOpen,
				"menu-dragging": store.state.sidebarDragging,
				"userlist-open": store.state.userlistOpen,
			};
		});

		const debouncedResize = ref<DebouncedFunc<() => void>>();
		const dayChangeTimeout = ref<any>();

		const escapeKey = () => {
			eventbus.emit("escapekey");
		};

		const toggleSidebar = (e: ExtendedKeyboardEvent) => {
			if (isIgnoredKeybind(e)) {
				return true;
			}

			store.commit("toggleSidebar");

			return false;
		};

		const toggleUserList = (e: ExtendedKeyboardEvent) => {
			if (isIgnoredKeybind(e)) {
				return true;
			}

			store.commit("toggleUserlist");

			return false;
		};

		const toggleMentions = () => {
			if (store.state.networks.length !== 0) {
				eventbus.emit("mentions:toggle");
			}
		};

		const msUntilNextDay = () => {
			// Compute how many milliseconds are remaining until the next day starts
			const today = new Date();
			const tommorow = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate() + 1
			).getTime();

			return tommorow - today.getTime();
		};

		const prepareOpenStates = () => {
			const viewportWidth = window.innerWidth;
			let isUserlistOpen = storage.get("thelounge.state.userlist");

			if (viewportWidth > constants.mobileViewportPixels) {
				store.commit("sidebarOpen", storage.get("thelounge.state.sidebar") !== "false");
			}

			// If The Lounge is opened on a small screen (less than 1024px), and we don't have stored
			// user list state, close it by default
			if (viewportWidth >= 1024 && isUserlistOpen !== "true" && isUserlistOpen !== "false") {
				isUserlistOpen = "true";
			}

			store.commit("userlistOpen", isUserlistOpen === "true");
		};

		prepareOpenStates();

		// ЛАФЧЕ.ORG патч: слуша postMessage от родителския сайт за смяна
		// на светла/тъмна тема, синхронизирано с ☀/🌙 бутона на lafche.org
		const handleLafcheThemeMessage = (event: MessageEvent) => {
			if (event.data && event.data.type === "lafche-theme") {
				const theme = event.data.theme === "dark" ? "lafche-dark" : "lafche-light";
				void store.dispatch("settings/update", {name: "theme", value: theme, sync: true});
			}

			// ЛАФЧЕ.ORG: channels.html (топ 30 таблицата на сайта) праща тази
			// команда, когато потребителят кликне на канал, докато вече е в чата
			if (event.data && event.data.type === "lafche-join-channel" && event.data.channel) {
				const channelName = event.data.channel as string;
				const network = store.state.networks[0];

				if (network) {
					const existing = network.channels.find((c) => c.name === channelName);

					if (existing) {
						switchToChannel(existing);
					} else if (store.state.activeChannel) {
						socket.emit("input", {
							target: store.state.activeChannel.channel.id,
							text: "/join " + channelName,
						});
					}
				}
			}
		};

		// ЛАФЧЕ.ORG патч: глобален WHOIS listener - показва модален прозорец
		// вместо личен таб, независимо дали е тригнато от context menu-то или
		// от ръчно написана /whois команда в чата
		const handleWhoisMessage = (data: any) => {
			if (data && data.msg && data.msg.type === "whois" && data.msg.whois) {
				// ЛАФЧЕ.ORG: пропускаме отварянето на модала, ако тази WHOIS
				// заявка е вътрешна (mute/unmute/kickban By Host) - виж
				// suppressWhoisModalFor в contextMenu.ts
				if (suppressWhoisModalFor.has(data.msg.whois.nick)) {
					return;
				}

				eventbus.emit("whois:open", {
					whois: data.msg.whois,
					network: store.state.activeChannel ? store.state.activeChannel.network : null,
				});
			}
		};

		// ЛАФЧЕ.ORG патч: тих, зациклен звук + Media Session API - подобно на
		// abvchat.com/kiwiirc - мобилните браузъри/ОС третират активно media
		// playback различно от обикновена WebSocket връзка и НЕ прекъсват
		// заден фон таб толкова агресивно, докато "върви музика". Тригва се
		// при първото потребителско взаимодействие (autoplay policy изисква
		// user gesture).
		const lafcheSilentAudio = ref<HTMLAudioElement | null>(null);
		const lafcheKeepAliveInterval = ref<ReturnType<typeof setInterval> | null>(null);

		const startLafcheKeepAlive = () => {
			if (!lafcheSilentAudio.value) {
				return;
			}

			lafcheSilentAudio.value.src = "audio/silence.wav";
			lafcheSilentAudio.value.loop = true;
			lafcheSilentAudio.value.volume = 0.05;

			// ЛАФЧЕ.ORG: защитен слой - на мобилни браузъри нативния loop
			// понякога "засича" тихо след background suspend (OS-level
			// throttle), без JS-ът изобщо да разбере. ensurePlaying()
			// презапуска възпроизвеждането при нужда - викана от 'ended'
			// event-а (резервен механизъм), периодична проверка, и
			// OS play/pause бутоните (виж setActionHandler по-долу)
			const ensurePlaying = () => {
				if (!lafcheSilentAudio.value) {
					return;
				}

				if (lafcheSilentAudio.value.paused) {
					void lafcheSilentAudio.value.play().catch(() => {
						// autoplay блокиран - ще опитаме пак при следващото взаимодействие/проверка
					});
				}

				if ("mediaSession" in navigator) {
					navigator.mediaSession.playbackState = "playing";
				}
			};

			lafcheSilentAudio.value.addEventListener("ended", ensurePlaying);

			if (lafcheKeepAliveInterval.value) {
				clearInterval(lafcheKeepAliveInterval.value);
			}

			lafcheKeepAliveInterval.value = setInterval(ensurePlaying, 15000);

			ensurePlaying();

			if ("mediaSession" in navigator) {
				navigator.mediaSession.metadata = new MediaMetadata({
					title: "LAFCHE.ORG",
					artist: "Активна чат връзка",
					album: "lafche.org",
				});
				navigator.mediaSession.playbackState = "playing";

				// ЛАФЧЕ.ORG: OS/браузър play-pause бутоните (notification shade,
				// lock screen) не правеха нищо - никога не бяхме регистрирали
				// action handlers. Понеже този звук съществува само за да пази
				// таба "активен", ВИНАГИ презапускаме при опит за пауза/спиране
				navigator.mediaSession.setActionHandler("play", ensurePlaying);
				navigator.mediaSession.setActionHandler("pause", ensurePlaying);
				navigator.mediaSession.setActionHandler("stop", ensurePlaying);
			}
		};

		const lafcheFirstInteraction = () => {
			startLafcheKeepAlive();
			document.removeEventListener("click", lafcheFirstInteraction);
			document.removeEventListener("touchstart", lafcheFirstInteraction);
		};


		// ЛАФЧЕ.ORG: при истински socket.io reconnect (не първоначална
		// връзка) - ако сме изгубили мрежовото си състояние (public
		// режим не пази сесия сървърно), автоматично "препращаме" пак
		// nick/join/NickServ парола от sessionStorage (записани в
		// Connect.vue при първоначалния connect), ако не са по-стари
		// от 15 минути
		const attemptLafcheReconnectRestore = () => {
			if (store.state.networks.length > 0) {
				return;
			}

			const raw = sessionStorage.getItem("lafche_reconnect_info");

			if (!raw) {
				return;
			}

			let saved: {nick?: string; join?: string; saslPassword?: string; savedAt?: number};

			try {
				saved = JSON.parse(raw);
			} catch (e) {
				sessionStorage.removeItem("lafche_reconnect_info");
				return;
			}

			const fifteenMinutes = 15 * 60 * 1000;

			if (!saved.savedAt || Date.now() - saved.savedAt > fifteenMinutes) {
				sessionStorage.removeItem("lafche_reconnect_info");
				return;
			}

			const restoreDefaults = Object.assign(
				{},
				store.state.serverConfiguration?.defaults,
				{nick: saved.nick, join: saved.join}
			);

			socket.emit("network:new", restoreDefaults);

			if (saved.saslPassword) {
				setTimeout(() => {
					const nets = store.state.networks;
					const net = nets[nets.length - 1];

					if (net && net.channels && net.channels[0]) {
						socket.emit("input", {
							target: net.channels[0].id,
							text: "/msg NickServ identify " + saved.saslPassword,
						});
					}
				}, 2000);
			}
		};

		onMounted(() => {
			socket.on("msg", handleWhoisMessage);
			socket.io.on("reconnect", attemptLafcheReconnectRestore);
			eventbus.on("lafche:reconnect-restore", attemptLafcheReconnectRestore);

			Mousetrap.bind("esc", escapeKey);
			Mousetrap.bind("alt+u", toggleUserList);
			Mousetrap.bind("alt+s", toggleSidebar);
			Mousetrap.bind("alt+m", toggleMentions);

			window.addEventListener("message", handleLafcheThemeMessage);

			document.addEventListener("click", lafcheFirstInteraction, {once: true});
			document.addEventListener("touchstart", lafcheFirstInteraction, {once: true});

			debouncedResize.value = throttle(() => {
				eventbus.emit("resize");
			}, 100);

			window.addEventListener("resize", debouncedResize.value, {passive: true});

			// Emit a daychange event every time the day changes so date markers know when to update themselves
			const emitDayChange = () => {
				eventbus.emit("daychange");
				// This should always be 24h later but re-computing exact value just in case
				dayChangeTimeout.value = setTimeout(emitDayChange, msUntilNextDay());
			};

			dayChangeTimeout.value = setTimeout(emitDayChange, msUntilNextDay());
		});

		onBeforeUnmount(() => {
			socket.off("msg", handleWhoisMessage);

			Mousetrap.unbind("esc");
			Mousetrap.unbind("alt+u");
			Mousetrap.unbind("alt+s");
			Mousetrap.unbind("alt+m");

			window.removeEventListener("message", handleLafcheThemeMessage);
			document.removeEventListener("click", lafcheFirstInteraction);
			document.removeEventListener("touchstart", lafcheFirstInteraction);

			if (debouncedResize.value) {
				window.removeEventListener("resize", debouncedResize.value);
			}

			if (dayChangeTimeout.value) {
				clearTimeout(dayChangeTimeout.value);
			}
		});

		return {
			viewportClasses,
			escapeKey,
			toggleSidebar,
			toggleUserList,
			toggleMentions,
			store,
			overlay,
			loungeWindow,
			imageViewer,
			contextMenu,
			confirmDialog,
			lafcheSilentAudio,
		};
	},
});
</script>
